/** `/gis-queue` — the GIS triage queue's core page (GIS Phase 2A, Task 10,
 *  spec §6). Filters + saved-view presets (`FilterBar`), per-type counts
 *  chips (`counts_by_type` — never client-recomputed), a paginated list of
 *  `FindingRow`s, and a feedback banner that surfaces every action outcome —
 *  success or error — so nothing resolves silently (UX-review Stage-1).
 *
 *  Evidence panels and mini-maps (Task 11a) live inside `FindingRow`/
 *  `EvidencePanel`. Task 11b adds two more surfaces at this top level:
 *   - `BulkBar` — rendered between the filter card and the findings list,
 *     visible only once a filter is active (spec §4: a bulk sweep always
 *     targets the ENTIRE matched set, never the unfiltered queue). Reuses
 *     this page's own feedback banner + findings-query invalidation on a
 *     successful execute, the same bubble-up `FindingRow`'s single-row
 *     actions already use.
 *   - `CrPanel` — a second tab ("Change requests") alongside the findings
 *     list ("Findings", the default so every existing single-row test that
 *     never touches the tab strip keeps passing unmodified). CRs are a
 *     different table (`gis_change_requests`) with their own filters/
 *     pagination/detail — not folded into the findings list or its query
 *     state. */

import { useCallback, useMemo, useState } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { useAuth } from '../../hooks/useAuth.ts'
import { exportCsv } from '../../utils/csv.ts'
import { getFindings } from './api.ts'
import { friendlyError } from '../../utils/friendlyError.ts'
import { BulkBar } from './BulkBar.tsx'
import { CrPanel } from './CrPanel.tsx'
import { FilterBar, PRESET_QUERIES, type PresetKey } from './FilterBar.tsx'
import { FindingRow } from './FindingRow.tsx'
import { WorklistsPanel } from './WorklistsPanel.tsx'
import { csvFilename, findingCsvRows } from './worklists.ts'
import {
  ACTION_SPECS,
  DISCREPANCY_TYPES,
  DISCREPANCY_TYPE_LABELS,
  hasCapability,
  type BulkExecuteResponse,
  type DiscrepancyType,
  type Finding,
  type FindingAction,
  type FindingActionResponse,
  type FindingsQuery,
} from './types.ts'

const PAGE_SIZE = 50

/** Backend clamp on GET /gis-queue/findings + /gis-queue/crs page size
 *  (api/v1/gis_queue.py: "Page size, clamped to 500."). */
const EXPORT_LIMIT = 500

type QueueTab = 'findings' | 'crs' | 'worklists'

// =============================================================================
// Per-type counts chips — rendered from `counts_by_type` (spec §4's
// invariant: every filter EXCEPT `type` still constrains it), never
// recomputed client-side from the current page's items.
// =============================================================================

function TypeCountChips({
  countsByType,
  total,
  activeType,
  onSelect,
}: {
  countsByType: Record<string, number>
  total: number
  activeType: DiscrepancyType | undefined
  onSelect: (type: DiscrepancyType | undefined) => void
}) {
  return (
    <div className="flex flex-wrap gap-1.5" role="group" aria-label="Findings by type">
      <button
        type="button"
        onClick={() => onSelect(undefined)}
        aria-pressed={activeType === undefined}
        className={`text-xs px-2.5 py-1 rounded-full border transition-colors ${
          activeType === undefined
            ? 'bg-gray-800 border-gray-800 text-white'
            : 'bg-white border-gray-300 text-gray-700 hover:bg-gray-50'
        }`}
      >
        All ({total})
      </button>
      {DISCREPANCY_TYPES.map(type => {
        // The active type's own count lives on `total` (counts_by_type never
        // includes the type currently filtered on — that's the invariant
        // that makes every OTHER chip's count stay meaningful while one is
        // selected).
        const count = activeType === type ? total : (countsByType[type] ?? 0)
        if (count === 0 && activeType !== type) return null
        return (
          <button
            key={type}
            type="button"
            onClick={() => onSelect(activeType === type ? undefined : type)}
            aria-pressed={activeType === type}
            className={`text-xs px-2.5 py-1 rounded-full border transition-colors ${
              activeType === type
                ? 'bg-blue-600 border-blue-600 text-white'
                : 'bg-white border-gray-300 text-gray-700 hover:bg-gray-50'
            }`}
          >
            {DISCREPANCY_TYPE_LABELS[type]} ({count})
          </button>
        )
      })}
    </div>
  )
}

export function QueuePage() {
  const { user } = useAuth()
  const capabilities = useMemo(() => user?.capabilities ?? [], [user])
  const queryClient = useQueryClient()

  const [tab, setTab] = useState<QueueTab>('findings')
  const [filters, setFilters] = useState<FindingsQuery>({})
  const [activePreset, setActivePreset] = useState<PresetKey | null>(null)
  const [offset, setOffset] = useState(0)
  const [feedback, setFeedback] = useState<{ tone: 'success' | 'error'; message: string } | null>(null)
  const [exporting, setExporting] = useState(false)

  const query = useMemo<FindingsQuery>(() => ({ ...filters, limit: PAGE_SIZE, offset }), [filters, offset])

  const {
    data,
    isLoading,
    isError,
    error,
    refetch,
  } = useQuery({
    queryKey: ['gis-queue', 'findings', query],
    queryFn: () => getFindings(query),
    enabled: hasCapability(capabilities, 'gis.queue.view'),
  })

  const handleFiltersChange = useCallback((next: FindingsQuery) => {
    setFilters(next)
    setActivePreset(null)
    setOffset(0)
  }, [])

  const handlePresetSelect = useCallback((preset: PresetKey) => {
    setFilters(PRESET_QUERIES[preset])
    setActivePreset(preset)
    setOffset(0)
  }, [])

  const handleTypeSelect = useCallback((type: DiscrepancyType | undefined) => {
    setFilters(f => ({ ...f, type }))
    setActivePreset(null)
    setOffset(0)
  }, [])

  const handleActionSuccess = useCallback(
    (finding: Finding, response: FindingActionResponse) => {
      const subject = finding.entity_summary ?? `finding #${response.id}`
      const verb =
        ACTION_SPECS[response.action as FindingAction]?.label ?? response.action.replace(/_/g, ' ')
      setFeedback({
        tone: 'success',
        message: `${verb} applied to ${subject} — removed from the pending queue.`,
      })
      void queryClient.invalidateQueries({ queryKey: ['gis-queue', 'findings'] })
    },
    [queryClient],
  )

  const handleActionError = useCallback((message: string) => {
    setFeedback({ tone: 'error', message })
  }, [])

  const handleBulkExecuteSuccess = useCallback(
    (result: BulkExecuteResponse) => {
      setFeedback({
        tone: 'success',
        message: `Bulk sweep applied to ${result.count} finding${result.count === 1 ? '' : 's'} — batch #${result.batch_id}, removed from the pending queue.`,
      })
      setOffset(0)
      void queryClient.invalidateQueries({ queryKey: ['gis-queue', 'findings'] })
    },
    [queryClient],
  )

  const handleExportFindings = useCallback(async () => {
    setExporting(true)
    try {
      // Fresh fetch of the CURRENT FILTER's rows at the backend clamp — the
      // export is the matched set, never just the visible page.
      const page = await getFindings({ ...filters, limit: EXPORT_LIMIT, offset: 0 })
      exportCsv(csvFilename('gis-queue-findings'), findingCsvRows(page.items))
      setFeedback({
        tone: 'success',
        message:
          page.total > page.items.length
            ? `Exported the first ${page.items.length} of ${page.total} findings — narrow the filters to export the rest.`
            : `Exported ${page.items.length} finding${page.items.length === 1 ? '' : 's'} to CSV.`,
      })
    } catch {
      setFeedback({
        tone: 'error',
        message: "Couldn't export findings. Try again; if it keeps failing, tell the engineer.",
      })
    } finally {
      setExporting(false)
    }
  }, [filters])

  if (!hasCapability(capabilities, 'gis.queue.view')) {
    return (
      <div className="flex-1 overflow-auto p-6 bg-gray-50">
        <div className="max-w-3xl mx-auto text-sm text-gray-600">
          You don&apos;t have access to the GIS triage queue. Ask an administrator to grant the{' '}
          <code className="px-1 bg-gray-200 rounded">gis.queue.view</code> capability.
        </div>
      </div>
    )
  }

  const items = data?.items ?? []
  const total = data?.total ?? 0
  const countsByType = data?.counts_by_type ?? {}

  return (
    <div className="flex-1 overflow-auto p-6 bg-gray-50">
      <div className="max-w-5xl mx-auto">
        <div className="mb-4">
          <h2 className="text-lg font-semibold text-gray-800">GIS Triage Queue</h2>
          <p className="text-sm text-gray-500 mt-1">
            Findings the GIS sync flagged for a human decision. Acknowledge what&apos;s right, correct
            what isn&apos;t — every action lands an audit row.
          </p>
        </div>

        <div className="flex gap-1 mb-4" role="tablist" aria-label="GIS queue views">
          <button
            type="button"
            role="tab"
            aria-selected={tab === 'findings'}
            onClick={() => setTab('findings')}
            className={`text-sm px-3 py-1.5 rounded-t-md border-b-2 ${
              tab === 'findings'
                ? 'border-blue-600 text-blue-700 font-medium'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            Findings
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={tab === 'crs'}
            onClick={() => setTab('crs')}
            className={`text-sm px-3 py-1.5 rounded-t-md border-b-2 ${
              tab === 'crs'
                ? 'border-blue-600 text-blue-700 font-medium'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            Change requests
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={tab === 'worklists'}
            onClick={() => setTab('worklists')}
            className={`text-sm px-3 py-1.5 rounded-t-md border-b-2 ${
              tab === 'worklists'
                ? 'border-blue-600 text-blue-700 font-medium'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            Worklists
          </button>
        </div>

        {tab === 'crs' && <CrPanel capabilities={capabilities} />}

        {tab === 'worklists' && <WorklistsPanel />}

        {tab === 'findings' && (
          <>
            <div className="bg-white border border-gray-200 rounded-lg p-4 mb-4 space-y-3">
              <FilterBar
                filters={filters}
                onFiltersChange={handleFiltersChange}
                activePreset={activePreset}
                onPresetSelect={handlePresetSelect}
              />
              <TypeCountChips
                countsByType={countsByType}
                total={total}
                activeType={filters.type}
                onSelect={handleTypeSelect}
              />
            </div>

            <BulkBar filters={filters} capabilities={capabilities} onExecuteSuccess={handleBulkExecuteSuccess} />

            {feedback && (
              <div
                role={feedback.tone === 'error' ? 'alert' : 'status'}
                className={`mb-4 text-sm rounded-md px-3 py-2 border flex items-center justify-between ${
                  feedback.tone === 'error'
                    ? 'text-red-700 bg-red-50 border-red-200'
                    : 'text-green-700 bg-green-50 border-green-200'
                }`}
              >
                <span>{feedback.message}</span>
                <button type="button" onClick={() => setFeedback(null)} className="ml-3 text-xs underline shrink-0">
                  Dismiss
                </button>
              </div>
            )}

            <div className="bg-white border border-gray-200 rounded-lg p-4">
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-sm font-medium text-gray-700">
                  Findings <span className="text-gray-400 ml-1">({total})</span>
                </h3>
                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    onClick={() => void handleExportFindings()}
                    disabled={exporting || total === 0}
                    className="text-xs px-2 py-1 rounded border border-gray-300 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {exporting ? 'Exporting…' : 'Export CSV'}
                  </button>
                  <button
                    type="button"
                    onClick={() => void refetch()}
                    className="text-xs px-2 py-1 rounded border border-gray-300 hover:bg-gray-50"
                  >
                    Refresh
                  </button>
                </div>
              </div>

              {isLoading && <div className="text-sm text-gray-500 py-8 text-center">Loading...</div>}

              {isError && (
                <div className="text-sm text-red-600 py-8 text-center">
                  {friendlyError(error, 'load the queue')}
                </div>
              )}

              {!isLoading && !isError && items.length === 0 && (
                <div className="text-sm text-gray-500 py-8 text-center">
                  No findings match the current filters.
                </div>
              )}

              {!isLoading && !isError && items.length > 0 && (
                <>
                  <div className="space-y-2">
                    {items.map(finding => (
                      <FindingRow
                        key={finding.discrepancy.id}
                        finding={finding}
                        capabilities={capabilities}
                        onActionSuccess={handleActionSuccess}
                        onActionError={handleActionError}
                      />
                    ))}
                  </div>

                  {total > PAGE_SIZE && (
                    <div className="flex items-center justify-between mt-4 pt-3 border-t border-gray-100">
                      <button
                        type="button"
                        onClick={() => setOffset(o => Math.max(0, o - PAGE_SIZE))}
                        disabled={offset === 0}
                        className="text-sm px-3 py-1 rounded border border-gray-300 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        Previous
                      </button>
                      <span className="text-xs text-gray-500">
                        {offset + 1}-{Math.min(offset + PAGE_SIZE, total)} of {total}
                      </span>
                      <button
                        type="button"
                        onClick={() => setOffset(o => o + PAGE_SIZE)}
                        disabled={offset + PAGE_SIZE >= total}
                        className="text-sm px-3 py-1 rounded border border-gray-300 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        Next
                      </button>
                    </div>
                  )}
                </>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  )
}
